/**
 * Sprint 6 · Semana 2 — Copilot de Configuración del Wizard
 *
 * Panel lateral que platica con el usuario en lenguaje natural y traduce
 * su descripción a cambios concretos en las assumptions. Muestra preview
 * de cambios antes de aplicar (opt-in por cada cambio).
 *
 * Reutiliza: /api/copilot/wizard-configure y /api/copilot/wizard-apply
 */
import { useState, useRef, useEffect } from 'react';
import apiClient from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { Bot, Send, Sparkles, ChevronRight, ChevronLeft, Check, X, Loader2, MessageSquare, AlertTriangle } from 'lucide-react';
import { LABEL_MAP, UNIT_MAP } from './helpers';

const formatValue = (key, value) => {
  const unit = UNIT_MAP[key];
  if (unit === '%') return `${(Number(value) * 100).toFixed(1)}%`;
  if (unit === 'MXN' || unit === 'MXN/m²') return `$${Number(value).toLocaleString('es-MX', { maximumFractionDigits: 0 })}`;
  if (unit === 'meses') return `${value} meses`;
  return String(value);
};

const SUGGESTED_PROMPTS = [
  'Explícame el aforo (LTC) y cómo lo interpreta el motor',
  'Tengo un puente con línea de $30M, aforo 65%, plazo 30 meses, tasa 13% anual',
  'Voy a vender 40 casas a $3.5M cada una, con preventa del 30%',
  'Configura una cobranza estándar residencial: 20% enganche y 80% escrituración',
];

export function WizardCopilot({ projectId, scenarioId, onChangesApplied }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);  // {role: 'user'|'assistant', content, changes?, warnings?, followUp?}
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [applyingIdx, setApplyingIdx] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  const sendMessage = async (text) => {
    const msg = (text || input).trim();
    if (!msg || sending) return;

    setMessages((m) => [...m, { role: 'user', content: msg }]);
    setInput('');
    setSending(true);

    try {
      const { data } = await apiClient.post('/copilot/wizard-configure', {
        project_id: projectId,
        scenario_id: scenarioId,
        message: msg,
        history: messages.slice(-8).map((m) => ({ role: m.role, content: m.content })),
      });
      setMessages((m) => [
        ...m,
        {
          role: 'assistant',
          content: data.reply || 'Sin respuesta',
          changes: data.changes || [],
          warnings: data.warnings || [],
          followUp: data.follow_up_question,
          appliedChanges: null,  // se llena cuando se aplican
        },
      ]);
    } catch (err) {
      setMessages((m) => [...m, { role: 'assistant', content: `Error: ${err.response?.data?.detail || err.message}`, changes: [], warnings: [] }]);
      toast.error('El copilot no pudo procesar tu mensaje');
    }
    setSending(false);
  };

  const applyChanges = async (msgIdx, selectedChanges) => {
    if (!selectedChanges || selectedChanges.length === 0) return;
    setApplyingIdx(msgIdx);
    try {
      const { data } = await apiClient.post('/copilot/wizard-apply', {
        project_id: projectId,
        scenario_id: scenarioId,
        changes: selectedChanges,
      });
      toast.success(`Aplicados ${data.applied} cambio${data.applied === 1 ? '' : 's'}`);
      setMessages((m) => m.map((msg, i) => i === msgIdx ? { ...msg, appliedChanges: selectedChanges.map(c => c.key) } : msg));
      onChangesApplied?.();
    } catch (err) {
      toast.error('No se pudieron aplicar los cambios');
    }
    setApplyingIdx(null);
  };

  return (
    <>
      {/* Botón flotante para abrir */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed right-4 bottom-20 z-40 flex items-center gap-2 rounded-full px-4 py-2.5 shadow-lg transition-all hover:scale-105"
          style={{ background: 'linear-gradient(135deg, #6366F1, #8B5CF6)', color: 'white' }}
          data-testid="wizard-copilot-toggle-open"
        >
          <Sparkles className="h-4 w-4" />
          <span className="text-sm font-medium">Copilot IA</span>
        </button>
      )}

      {/* Panel lateral */}
      {open && (
        <div className="fixed right-0 top-0 bottom-0 z-50 w-full sm:w-[420px] bg-card border-l border-border shadow-2xl flex flex-col" data-testid="wizard-copilot-panel">
          {/* Header */}
          <div className="shrink-0 px-4 py-3 border-b border-border flex items-center justify-between" style={{ background: 'linear-gradient(135deg, #EEF2FF, #F5F3FF)' }}>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #6366F1, #8B5CF6)' }}>
                <Bot className="h-4 w-4 text-white" />
              </div>
              <div>
                <h3 className="text-sm font-display font-semibold" style={{ color: '#4F46E5' }}>Copilot de Configuración</h3>
                <p className="text-[10px] text-muted-foreground">Platícame tu escenario en lenguaje natural</p>
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="w-7 h-7 rounded-lg hover:bg-white/60 flex items-center justify-center"
              data-testid="wizard-copilot-close"
            >
              <ChevronRight className="h-4 w-4" style={{ color: '#4F46E5' }} />
            </button>
          </div>

          {/* Mensajes */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-3 space-y-3">
            {messages.length === 0 && (
              <div className="space-y-3">
                <div className="rounded-2xl px-4 py-3" style={{ background: '#F5F3FF', border: '1px solid #DDD6FE' }}>
                  <p className="text-xs" style={{ color: '#5B21B6' }}>
                    Hola. Soy tu copilot para configurar el wizard. Describe tu escenario en palabras (crédito, ventas, cobranza) y yo lo traduzco a variables. También puedo explicarte conceptos financieros.
                  </p>
                </div>
                <div className="space-y-1.5">
                  <p className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Sugerencias</p>
                  {SUGGESTED_PROMPTS.map((p, i) => (
                    <button
                      key={i}
                      onClick={() => sendMessage(p)}
                      disabled={sending}
                      className="w-full text-left rounded-xl px-3 py-2 text-xs hover:bg-muted/40 transition-colors border border-border/40"
                      data-testid={`wizard-copilot-suggestion-${i}`}
                    >
                      <MessageSquare className="h-3 w-3 inline mr-1.5 text-muted-foreground" />
                      {p}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((msg, idx) => (
              <MessageBubble
                key={idx}
                msg={msg}
                onApply={(selected) => applyChanges(idx, selected)}
                applying={applyingIdx === idx}
                onSendFollowUp={() => msg.followUp && sendMessage(msg.followUp)}
              />
            ))}

            {sending && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground px-2">
                <Loader2 className="h-3 w-3 animate-spin" />
                Pensando...
              </div>
            )}
          </div>

          {/* Input */}
          <div className="shrink-0 border-t border-border p-3">
            <div className="flex items-end gap-2">
              <Textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    sendMessage();
                  }
                }}
                placeholder="Ej: puente $30M al 65%, plazo 24m, tasa 12%..."
                className="min-h-[60px] max-h-[120px] resize-none text-sm rounded-xl"
                disabled={sending}
                data-testid="wizard-copilot-input"
              />
              <Button
                onClick={() => sendMessage()}
                disabled={sending || !input.trim()}
                size="icon"
                className="h-10 w-10 rounded-xl shrink-0"
                style={{ background: 'linear-gradient(135deg, #6366F1, #8B5CF6)' }}
                data-testid="wizard-copilot-send"
              >
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </Button>
            </div>
            <p className="text-[10px] text-muted-foreground mt-2 px-1">
              Enter para enviar · Shift+Enter para nueva línea · Powered by GPT-4o
            </p>
          </div>
        </div>
      )}
    </>
  );
}


function MessageBubble({ msg, onApply, applying, onSendFollowUp }) {
  const [selected, setSelected] = useState(() => new Set((msg.changes || []).map((c) => c.key)));

  const isUser = msg.role === 'user';
  const applied = msg.appliedChanges;

  if (isUser) {
    return (
      <div className="flex justify-end">
        <div className="max-w-[85%] rounded-2xl px-3 py-2 text-sm" style={{ background: '#6366F1', color: 'white' }}>
          {msg.content}
        </div>
      </div>
    );
  }

  const toggleChange = (key) => {
    const next = new Set(selected);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setSelected(next);
  };

  const selectedChanges = (msg.changes || []).filter((c) => selected.has(c.key));

  return (
    <div className="flex justify-start">
      <div className="max-w-[92%] space-y-2">
        {/* Respuesta narrativa */}
        <div className="rounded-2xl px-3 py-2.5 text-sm" style={{ background: 'white', border: '1px solid #E2E8F0' }}>
          <div className="text-xs whitespace-pre-wrap" dangerouslySetInnerHTML={{ __html: renderInlineMarkdown(msg.content) }} />
        </div>

        {/* Advertencias */}
        {(msg.warnings || []).length > 0 && (
          <div className="space-y-1">
            {msg.warnings.map((w, i) => (
              <div key={i} className="rounded-lg px-2.5 py-1.5 text-[11px] flex items-start gap-1.5" style={{ background: '#FFFBEB', border: '1px solid #FDE68A', color: '#92400E' }}>
                <AlertTriangle className="h-3 w-3 shrink-0 mt-0.5" />
                <span>{w}</span>
              </div>
            ))}
          </div>
        )}

        {/* Preview de cambios propuestos */}
        {(msg.changes || []).length > 0 && !applied && (
          <div className="rounded-xl border overflow-hidden" style={{ borderColor: '#DDD6FE' }}>
            <div className="px-3 py-2 flex items-center justify-between" style={{ background: '#F5F3FF' }}>
              <span className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: '#5B21B6' }}>
                Cambios propuestos ({msg.changes.length})
              </span>
              <div className="flex items-center gap-1.5">
                <button
                  onClick={() => setSelected(new Set(msg.changes.map((c) => c.key)))}
                  className="text-[10px] font-medium hover:underline"
                  style={{ color: '#6366F1' }}
                  data-testid="wizard-copilot-select-all"
                >
                  Todos
                </button>
                <span className="text-[10px] text-muted-foreground">·</span>
                <button
                  onClick={() => setSelected(new Set())}
                  className="text-[10px] font-medium hover:underline"
                  style={{ color: '#6366F1' }}
                >
                  Ninguno
                </button>
              </div>
            </div>
            <div className="divide-y divide-border/50">
              {msg.changes.map((c, i) => {
                const isSel = selected.has(c.key);
                return (
                  <button
                    key={i}
                    onClick={() => toggleChange(c.key)}
                    className="w-full text-left px-3 py-2 flex items-start gap-2 hover:bg-muted/30 transition-colors"
                    data-testid={`wizard-copilot-change-${c.key}`}
                  >
                    <div className={`w-4 h-4 rounded shrink-0 mt-0.5 flex items-center justify-center border`} style={{
                      background: isSel ? '#6366F1' : 'white',
                      borderColor: isSel ? '#6366F1' : '#CBD5E1',
                    }}>
                      {isSel && <Check className="h-3 w-3 text-white" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-xs font-medium truncate">{LABEL_MAP[c.key] || c.key}</span>
                        <span className="text-xs font-semibold tabular-nums shrink-0" style={{ color: '#4F46E5' }}>
                          {formatValue(c.key, c.value)}
                        </span>
                      </div>
                      {c.note && <p className="text-[10px] text-muted-foreground mt-0.5 truncate">{c.note}</p>}
                    </div>
                  </button>
                );
              })}
            </div>
            <div className="px-3 py-2 flex items-center gap-2" style={{ background: '#FAFAFA' }}>
              <Button
                onClick={() => onApply(selectedChanges)}
                disabled={applying || selectedChanges.length === 0}
                size="sm"
                className="h-8 text-xs flex-1"
                style={{ background: 'linear-gradient(135deg, #6366F1, #8B5CF6)' }}
                data-testid="wizard-copilot-apply"
              >
                {applying ? <Loader2 className="h-3 w-3 mr-1.5 animate-spin" /> : <Check className="h-3 w-3 mr-1.5" />}
                Aplicar {selectedChanges.length > 0 ? `(${selectedChanges.length})` : ''}
              </Button>
            </div>
          </div>
        )}

        {/* Cambios ya aplicados */}
        {applied && applied.length > 0 && (
          <div className="rounded-lg px-2.5 py-1.5 text-[11px] flex items-center gap-1.5" style={{ background: '#F0FDF4', border: '1px solid #86EFAC', color: '#166534' }}>
            <Check className="h-3 w-3" />
            <span>Aplicado{applied.length > 1 ? 's' : ''}: {applied.length} campo{applied.length > 1 ? 's' : ''}</span>
          </div>
        )}

        {/* Follow-up sugerido */}
        {msg.followUp && (
          <button
            onClick={onSendFollowUp}
            className="text-left w-full rounded-lg px-2.5 py-1.5 text-[11px] hover:bg-muted/30 transition-colors border border-dashed"
            style={{ borderColor: '#CBD5E1', color: '#475569' }}
            data-testid="wizard-copilot-followup"
          >
            <span className="text-muted-foreground">Continuar: </span>
            {msg.followUp}
          </button>
        )}
      </div>
    </div>
  );
}

// Renderiza **bold** y `code` inline sin librerías extra
function renderInlineMarkdown(text) {
  if (!text) return '';
  const escaped = String(text)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  return escaped
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    .replace(/`([^`]+)`/g, '<code style="background:#F1F5F9;padding:1px 4px;border-radius:3px;font-size:11px;">$1</code>');
}
